import { create } from "zustand";

/**
 * Procurement AI Tribunal Store (v1)
 * 
 * Manages procurement tribunal cases, vendor violations,
 * severity scoring, and automated tribunal decisions.
 */
export interface TribunalStoreState {
  caseID: string;
  vendorID: string;
  severityScore: number; // 0-100
  meshLinkHash: string;
  evidenceCount: number;
  jurisdiction: "EU" | "National" | "EEA";
  tribunalDecision: "pending" | "warning" | "fine" | "disqualification";
  appealWindowDays: number;
  remediationDemandEUR: number;

  // Actions
  setCaseID: (id: string) => void;
  setVendorID: (id: string) => void;
  setSeverityScore: (score: number) => void;
  setMeshLinkHash: (hash: string) => void;
  setEvidenceCount: (count: number) => void;
  setJurisdiction: (jurisdiction: "EU" | "National" | "EEA") => void;

  // Actions
  generateDecision: () => void;
  appealCase: () => void;

  // Derived outputs
  getWeightedSeverity: () => number;
  getDecision: () => "pending" | "warning" | "fine" | "disqualification";

  // Export
  exportTribunalCase: () => void;
}

export const useTribunalStore = create<TribunalStoreState>((set, get) => ({
  caseID: "",
  vendorID: "",
  severityScore: 0,
  meshLinkHash: "",
  evidenceCount: 0,
  jurisdiction: "EU",
  tribunalDecision: "pending",
  appealWindowDays: 0,
  remediationDemandEUR: 0,

  setCaseID: (id) => set({ caseID: id }),
  setVendorID: (id) => set({ vendorID: id }),
  setSeverityScore: (score) => set({ severityScore: Math.max(0, Math.min(100, score)) }),
  setMeshLinkHash: (hash) => set({ meshLinkHash: hash }),
  setEvidenceCount: (count) => set({ evidenceCount: Math.max(0, count) }),
  setJurisdiction: (jurisdiction) => set({ jurisdiction }),

  generateDecision: () => {
    const state = get();
    if (!state.vendorID || state.severityScore === 0) return;

    const weightedSeverity = state.getWeightedSeverity();
    let decision: "pending" | "warning" | "fine" | "disqualification" = "pending";

    if (weightedSeverity < 40) {
      decision = "warning";
    } else if (weightedSeverity <= 70) {
      decision = "fine";
    } else {
      decision = "disqualification";
    }

    const remediationDemandEUR = weightedSeverity * 1500;

    set({
      tribunalDecision: decision,
      appealWindowDays: 14,
      remediationDemandEUR,
    });
  },

  appealCase: () => {
    const state = get();
    if (state.tribunalDecision === "pending" || state.appealWindowDays === 0) return;

    const reduced = state.remediationDemandEUR * 0.85;
    set({ remediationDemandEUR: reduced });
  },

  getWeightedSeverity: () => {
    const state = get();
    return state.severityScore + state.evidenceCount * 3;
  },

  getDecision: () => {
    return get().tribunalDecision;
  },

  exportTribunalCase: () => {
    const state = get();
    const report = {
      caseID: state.caseID || `TRIBUNAL_${Date.now()}`,
      vendorID: state.vendorID,
      severityScore: state.severityScore,
      meshLinkHash: state.meshLinkHash,
      evidenceCount: state.evidenceCount,
      jurisdiction: state.jurisdiction,
      tribunalDecision: state.tribunalDecision,
      appealWindowDays: state.appealWindowDays,
      remediationDemandEUR: state.remediationDemandEUR,
      weightedSeverity: state.getWeightedSeverity(),
    };

    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tribunalCase_${dateStr}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
}));











